import {Component, EventEmitter, Input, Output, OnInit, SimpleChanges, OnChanges} from '@angular/core';
import { FormsModule} from '@angular/forms';
import { DropDownComponent} from "../drop-down/drop-down.component";
import { PaymentMethods, PaymentMode } from '../../models/payment-method.model';
import { BankService} from '../../shared/services/bank.service';
import {AdditionalPACData, PaidPaymentCollection, PaymentFrequency} from '../../models/additional-pac-data.model';
import { CommonModule } from '@angular/common';
import {AppConstants} from '../../shared/app-constants';
import {getDefaultPaymentAmount, restrictToTwoDecimals} from '../../shared/utils/default-amount-utils';

@Component({
  selector: 'app-cheque-deposit',
  standalone: true,
  imports: [DropDownComponent, FormsModule, CommonModule],
  templateUrl: './cheque-deposit.component.html',
  styleUrls: ['./cheque-deposit.component.scss']
})
export class ChequeDepositComponent implements OnInit, OnChanges { //implement onchanges
  @Input() isSelected: boolean = false;
  @Input() paymentMethodInfo!: PaymentMethods;
  @Input() pacDetails!: AdditionalPACData;
  @Output() cardClick = new EventEmitter<string | null>(); //output bindings, including aliases, should not be named "on", nor prefixed with it
  @Output() pacDetailsUpdated = new EventEmitter<any>();
  chequeDetails!: PaidPaymentCollection;
  bankDatasource: any[] = [];
  isAcknowledged: boolean = false;
  enableAmountCheckbox: boolean = false;
  isInputEnabled: boolean = false;
  chequeAmount: number | undefined = 0;
  enteredChequeAmount: string = '';
  amountZero: boolean = false;
  messageToShow: string = '';
  note: string = AppConstants.CHEQUE_NOTE

  constructor(private readonly bankService: BankService) {
  }

  ngOnInit(): void {
    this.chequeAmount = getDefaultPaymentAmount(this.pacDetails);
    this.enteredChequeAmount = AppConstants.inputAmountFormatted(this.chequeAmount) ?? '';
    this.chequeDetails = {
      chequeNumber: '',
      paymentAmount: null,
      bankCode: '',
      paymentMode: PaymentMode.CHEQUE,
      pacNumber: this.pacDetails.pacNumber
    };
    this.bankService.fetchActiveBanks().subscribe({
      next: (banks) => {
        this.bankDatasource = banks.map(bank => ({
          ...bank,
          name: bank.bankName,
          bankCode: bank.bankCode
        }));
      },

      error: (err) => {
      }
    });
    this.validateAmount(this.chequeAmount);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pacDetails.balancePayable']?.currentValue != null) { //prefer optional using
      this.chequeAmount = this.pacDetails.balancePayable;
    }
  }
  onSelectPaymentMethod(paymentMethod: string | null) {
    this.cardClick.emit(paymentMethod);
  }

  onBankSelected(bank: any) {
    this.chequeDetails.bankCode = bank.bankCode;
  }

  onAmountBlur(): void {
    const amount = Number.parseFloat(this.enteredChequeAmount); //prefer number.parsefloat over parsefloat
    if (!Number.isNaN(amount)) { //prefer number.isnan over isnan
      this.enteredChequeAmount = amount.toFixed(2);
    }
  }

  showAmountFormatted(value: number | null | undefined): string {
    return AppConstants.showAmountFormatted(value);
  }

  onSubmit() {
    this.chequeDetails.paymentAmount = Number.parseFloat(this.enteredChequeAmount) || 0;
    const requestBody: PaidPaymentCollection = {
      paymentMode: this.chequeDetails.paymentMode,
      transactionId: this.chequeDetails.chequeNumber,
      paymentAmount: this.chequeDetails.paymentAmount ?? 0,
      bankCode: this.chequeDetails.bankCode,
      pacNumber: this.pacDetails.pacNumber
    };
    this.pacDetailsUpdated.emit(requestBody)
    this.chequeDetails.chequeNumber = '';
    this.chequeDetails.paymentAmount = null;
    this.chequeDetails.bankCode = '';
    this.isAcknowledged = false;
    this.isSelected = false;
  }

  isFormValid(): boolean {
    const isChequeNumberValid = /^\d{1,20}$/.test(<string>this.chequeDetails.chequeNumber);
    const isAmountValid = !this.amountZero && this.messageToShow !== 'PAYABLE_EXCEEDED' && this.messageToShow !== 'LIMIT_EXCEEDED';
    const isBankSelected = !!this.chequeDetails.bankCode;
    return isChequeNumberValid && isAmountValid && isBankSelected;
  }


  toggleAmountInput(event: any) {
    const input = event.target as HTMLInputElement;
    this.isInputEnabled = input.checked;
    this.enableAmountCheckbox = input.checked;
    const isNotInstallment = this.pacDetails?.paymentFrequency !== PaymentFrequency.I; //unexpected negated condition
    if (!input.checked) {
      this.chequeAmount = isNotInstallment
        ? this.pacDetails?.balancePayable
        : this.pacDetails?.selectedAmountPayable;
      this.enteredChequeAmount = AppConstants.inputAmountFormatted(this.chequeAmount) ?? '';
      this.validateAmount(Number(this.chequeAmount));
    }
  }

  isConfirmDisabled(): boolean {
    return !this.isAcknowledged || !this.isFormValid();
  }

  onChequeAmountChange(event: Event): void {
    const sanitizedValue = restrictToTwoDecimals(event);
    this.chequeAmount = sanitizedValue ? Number.parseFloat(sanitizedValue) : 0;
    this.validateAmount(this.chequeAmount);
  }

  private validateAmount(amount: number | null): void {
    let totalLimit = this.paymentMethodInfo?.totalLimit ?? 0;
    this.messageToShow = '';
    this.amountZero = false;
    if (amount != undefined && amount <= 0) {
      this.amountZero = true;
    }
    if (amount !== null) {
      if (amount > this.pacDetails.balancePayable) {
        this.messageToShow = 'PAYABLE_EXCEEDED';
      } else if (totalLimit !== 0 && amount > totalLimit) {
        this.messageToShow = 'LIMIT_EXCEEDED';
      }
    }
  }

  allowOnlyNumbers(event: KeyboardEvent): void {
    const charCode = event.which ? event.which : event.keyCode; // NOSONAR
    const charStr = String.fromCodePoint(charCode);
    // Allow only digits 0-9
    if (!/^\d$/.test(charStr)) {
      event.preventDefault();
    }
  }

  handleDropdownKey(event: any) {
    return AppConstants.handleDropdownKey(event);
  }
}
